import type { ReactNode } from 'react';
import { cn } from '../cn';

export type SpinnerSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<SpinnerSize, string> = {
  sm: 'h-4 w-4 border-2',
  md: 'h-8 w-8 border-2',
  lg: 'h-12 w-12 border-[3px]',
};

export interface SpinnerProps {
  size?: SpinnerSize;
  /** Screen-reader text. When omitted the spinner is decorative. */
  label?: string;
  className?: string;
}

export function Spinner({ size = 'md', label, className }: SpinnerProps) {
  return (
    <span
      className={cn(
        'inline-block shrink-0 animate-spin rounded-circle border-current border-t-transparent',
        SIZE_CLASSES[size],
        className,
      )}
      {...(label ? { role: 'status', 'aria-label': label } : { 'aria-hidden': true })}
    />
  );
}

export interface LoadingStateProps {
  label?: string;
  /** `spinner` (default) or a pulsing text label only. */
  indicator?: 'spinner' | 'pulse';
  children?: ReactNode;
  className?: string;
}

/** Centered page/section loading indicator. */
export function LoadingState({
  label,
  indicator = 'spinner',
  children,
  className,
}: LoadingStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-4 py-20 text-accent', className)}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      {indicator === 'spinner' ? <Spinner size="lg" /> : null}
      {children ??
        (label ? (
          <p
            className={cn(
              'm-0 text-text-secondary',
              indicator === 'pulse' ? 'animate-pulse' : 'text-sm',
            )}
          >
            {label}
          </p>
        ) : null)}
    </div>
  );
}

export default Spinner;
